import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Zap, 
  TrendingUp, 
  AlertTriangle, 
  RefreshCw, 
  Play, 
  Pause,
  Settings,
  BarChart3,
  Activity,
  DollarSign,
  Clock,
  Target,
  Shield
} from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import { analysisService } from '../services/api';
import { 
  formatCurrency, 
  formatPercent, 
  formatDateTime, 
  getRecommendationColor,
  getRecommendationText,
  getConfidenceColor 
} from '../utils/helpers';

const ComprehensiveAIDashboard = () => {
  const [data, setData] = useState({
    schedulerStatus: null,
    portfolio: null,
    marketSummary: null,
    holdings: null,
    aiRecommendations: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRealTimeActive, setIsRealTimeActive] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30초
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const loadAllData = async () => {
    try {
      const [schedulerRes, portfolioRes, marketRes, holdingsRes, aiRes] = await Promise.allSettled([
        analysisService.getSchedulerStatus(),
        analysisService.getPortfolio(),
        analysisService.getMarketSummary(),
        analysisService.getHoldings(),
        analysisService.getAIRecommendations(),
      ]);

      setData({
        schedulerStatus: schedulerRes.status === 'fulfilled' ? schedulerRes.value.data : null,
        portfolio: portfolioRes.status === 'fulfilled' ? portfolioRes.value.data : null,
        marketSummary: marketRes.status === 'fulfilled' ? marketRes.value.data : null,
        holdings: holdingsRes.status === 'fulfilled' ? holdingsRes.value.data : null,
        aiRecommendations: aiRes.status === 'fulfilled' ? aiRes.value.data : null,
      });
      
      setLastUpdated(new Date());
    } catch (error) {
      console.error('AI Dashboard data load error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    let interval;
    if (isRealTimeActive) {
      interval = setInterval(loadAllData, refreshInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRealTimeActive, refreshInterval]);

  const toggleRealTime = () => {
    setIsRealTimeActive(!isRealTimeActive);
  };

  const tabs = [
    { id: 'overview', label: '📊 종합 현황', icon: BarChart3 },
    { id: 'realtime', label: '📡 실시간 모니터', icon: Activity },
    { id: 'recommendations', label: '🤖 AI 추천', icon: Brain },
    { id: 'analysis', label: '📈 분석 결과', icon: TrendingUp },
  ];

  const statusCards = [
    {
      title: '🤖 자동매매 시스템',
      value: data.schedulerStatus?.is_running ? 'ON' : 'OFF',
      status: data.schedulerStatus?.is_running ? 'success' : 'error',
      icon: Zap,
    },
    {
      title: '📈 오늘 수익률',
      value: formatPercent(data.portfolio?.today_return || 0),
      status: data.portfolio?.today_return > 0 ? 'success' : 'error',
      icon: TrendingUp,
    },
    {
      title: '💡 AI 추천 종목',
      value: data.aiRecommendations?.length || 0,
      status: 'info',
      icon: Brain,
    },
    {
      title: '🛒 오늘 주문',
      value: data.schedulerStatus?.today_orders || 0,
      status: 'info',
      icon: DollarSign,
    },
  ];

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex justify-center py-12">
            <Loading size="large" message="AI 분석 데이터를 불러오는 중..." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* 헤더 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              🧠 종합 AI 대시보드
            </h1>
            <p className="text-gray-600 mt-1">모든 AI 분석을 한눈에 확인하세요</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {lastUpdated && (
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="w-4 h-4 mr-1" />
                {lastUpdated.toLocaleTimeString()}
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <span className={`w-3 h-3 rounded-full ${isRealTimeActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
              <span className="text-sm text-gray-600">
                {isRealTimeActive ? '🟢 실시간 연결' : '🔴 연결 끊김'}
              </span>
            </div>
            
            <button
              onClick={toggleRealTime}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                isRealTimeActive 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isRealTimeActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{isRealTimeActive ? '일시정지' : '시작'}</span>
            </button>
            
            <button
              onClick={loadAllData}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>새로고침</span>
            </button>
          </div>
        </div>

        {/* 상태 카드들 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statusCards.map((card) => (
            <div key={card.title} className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                </div>
                <div className="ml-4">
                  <card.icon className="w-8 h-8 text-blue-500" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 탭 네비게이션 */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="space-y-6">
          {activeTab === 'overview' && <OverviewTab data={data} />}
          {activeTab === 'realtime' && <RealtimeTab data={data} isActive={isRealTimeActive} refreshInterval={refreshInterval} setRefreshInterval={setRefreshInterval} />}
          {activeTab === 'recommendations' && <RecommendationsTab data={data} />}
          {activeTab === 'analysis' && <AnalysisTab data={data} />}
        </div>
      </div>
    </Layout>
  );
};

// 종합 현황 탭
const OverviewTab = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {/* 포트폴리오 현황 */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 포트폴리오 현황</h3>
      {data.portfolio ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">총 평가금액</span>
            <span className="text-xl font-bold">{formatCurrency(data.portfolio.total_value || 0)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">수익률</span>
            <span className={`text-lg font-semibold ${data.portfolio.return_rate > 0 ? 'text-red-600' : 'text-blue-600'}`}>
              {formatPercent(data.portfolio.return_rate || 0)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">보유 종목 수</span>
            <span className="text-lg font-medium">{data.holdings?.length || 0}개</span>
          </div>
        </div>
      ) : (
        <p className="text-gray-500">포트폴리오 데이터를 불러올 수 없습니다.</p>
      )}
    </div>

    {/* AI 분석 요약 */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">🤖 AI 분석 요약</h3>
      {data.aiRecommendations ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">매수 추천</span>
            <span className="text-lg font-bold text-red-600">
              {data.aiRecommendations.filter(r => r.recommendation === 'BUY').length}개
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">매도 추천</span>
            <span className="text-lg font-bold text-blue-600">
              {data.aiRecommendations.filter(r => r.recommendation === 'SELL').length}개
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">보유 추천</span>
            <span className="text-lg font-bold text-gray-600">
              {data.aiRecommendations.filter(r => r.recommendation === 'HOLD').length}개
            </span>
          </div>
        </div>
      ) : (
        <p className="text-gray-500">AI 추천 데이터를 불러올 수 없습니다.</p>
      )}
    </div>
  </div>
);

// 실시간 모니터 탭
const RealtimeTab = ({ data, isActive, refreshInterval, setRefreshInterval }) => (
  <div className="space-y-6">
    {/* 설정 패널 */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">📡 실시간 모니터 설정</h3>
      <div className="flex items-center space-x-6">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">새로고침 간격:</label>
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10000}>10초</option>
            <option value={30000}>30초</option>
            <option value={60000}>1분</option>
            <option value={300000}>5분</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
          <span className="text-sm text-gray-600">
            {isActive ? '실시간 업데이트 중' : '업데이트 중지'}
          </span>
        </div>
      </div>
    </div>

    {/* 실시간 통계 */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 실시간 통계</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{data.aiRecommendations?.length || 0}</p>
          <p className="text-sm text-gray-600">분석 완료 종목</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-600">{data.aiRecommendations?.filter(r => r.recommendation === 'BUY').length || 0}</p>
          <p className="text-sm text-gray-600">매수 신호</p>
        </div>
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <p className="text-2xl font-bold text-red-600">{data.aiRecommendations?.filter(r => r.recommendation === 'SELL').length || 0}</p>
          <p className="text-sm text-gray-600">매도 신호</p>
        </div>
      </div>
    </div>
  </div>
);

// AI 추천 탭
const RecommendationsTab = ({ data }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900">🤖 AI 추천 종목</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">종목</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">추천</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">신뢰도</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">목표가</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">현재가</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">기대수익률</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.aiRecommendations?.map((item, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">{item.stock_name}</div>
                  <div className="text-sm text-gray-500">{item.stock_code}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRecommendationColor(item.recommendation)}`}>
                  {getRecommendationText(item.recommendation)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceColor(item.confidence)}`}>
                  {formatPercent(item.confidence)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatCurrency(item.target_price)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {formatCurrency(item.current_price)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <span className={`${item.expected_return > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                  {formatPercent(item.expected_return)}
                </span>
              </td>
            </tr>
          )) || (
            <tr>
              <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                AI 추천 데이터를 불러올 수 없습니다.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// 분석 결과 탭
const AnalysisTab = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {/* 시장 분석 */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 시장 분석</h3>
      {data.marketSummary ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">KOSPI</span>
            <span className="text-lg font-medium">{data.marketSummary.kospi || 'N/A'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">KOSDAQ</span>
            <span className="text-lg font-medium">{data.marketSummary.kosdaq || 'N/A'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">시장 상황</span>
            <span className="text-lg font-medium">{data.marketSummary.trend || '중립'}</span>
          </div>
        </div>
      ) : (
        <p className="text-gray-500">시장 데이터를 불러올 수 없습니다.</p>
      )}
    </div>

    {/* 리스크 분석 */}
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">🛡️ 리스크 분석</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">포트폴리오 위험도</span>
          <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">보통</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">변동성</span>
          <span className="text-lg font-medium">12.5%</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">최대 손실 예상</span>
          <span className="text-lg font-medium text-red-600">-8.2%</span>
        </div>
      </div>
    </div>
  </div>
);

export default ComprehensiveAIDashboard;